import React, { useState } from 'react';
import { playerDevelopmentNotesService } from '../../services/playerDevelopmentNotesService';
import type { PlayerDevelopmentNote } from '../../types/playerCard';
import { FileText, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

interface DevelopmentNotesProps {
  cardId: string;
  notes: PlayerDevelopmentNote[];
  onUpdate: () => void;
}

export const DevelopmentNotes: React.FC<DevelopmentNotesProps> = ({
  cardId,
  notes,
  onUpdate,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState({
    note_text: '',
    category: '',
    tags: [] as string[],
  });

  const handleAddNote = async () => {
    if (!formData.note_text.trim()) {
      toast.error('Note text is required');
      return;
    }

    try {
      await playerDevelopmentNotesService.createNote(cardId, {
        note_text: formData.note_text,
        category: formData.category || undefined,
        tags: formData.tags.length > 0 ? formData.tags : undefined,
      });

      toast.success('Note added successfully');
      setIsAdding(false);
      setFormData({ note_text: '', category: '', tags: [] });
      onUpdate();
    } catch (error) {
      console.error('Error creating note:', error);
      toast.error('Failed to add note');
    }
  };

  const handleDelete = async (noteId: string) => {
    if (!confirm('Are you sure you want to delete this note?')) return;

    try {
      await playerDevelopmentNotesService.deleteNote(noteId);
      toast.success('Note deleted');
      onUpdate();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FileText size={20} />
          Development Notes
        </h3>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={16} />
            Add Note
          </button>
        )}
      </div>

      {/* Add Note Form */}
      {isAdding && (
        <div className="bg-gray-50 rounded-lg p-6 space-y-4">
          <h4 className="font-semibold text-gray-900">New Note</h4>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note Text *
            </label>
            <textarea
              value={formData.note_text}
              onChange={(e) => setFormData({ ...formData, note_text: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              rows={4}
              placeholder="Enter your development note..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category (Optional)
            </label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="e.g., Skill Development, Behavior, etc."
            />
          </div>
          <div className="flex gap-2">
            <button onClick={handleAddNote} className="btn-primary">
              Save Note
            </button>
            <button
              onClick={() => {
                setIsAdding(false);
                setFormData({ note_text: '', category: '', tags: [] });
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Notes List */}
      {notes.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-600">No notes yet. Add one to track player development!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {notes.map((note) => (
            <div
              key={note.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  {note.category && (
                    <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium mb-2">
                      {note.category}
                    </span>
                  )}
                  <p className="text-gray-900 whitespace-pre-wrap">{note.note_text}</p>
                  {note.tags && note.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {note.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(note.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors ml-4"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="text-xs text-gray-500">
                {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

